import React, { useEffect, useState } from "react";
import { Search, Loader2, Check, X as XIcon, Sparkles, Zap, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function DomainSearch({ compact = false }) {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [live, setLive] = useState(null); // null=unknown, true=DNAPI live, false=mock
  const { addToCart } = useAuth();

  useEffect(() => {
    api.get("/domains/status").then(({ data }) => setLive(!!data.live)).catch(() => setLive(false));
  }, []);

  const anyLive = results.some((r) => r.live);

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/domains/search", { query: q.trim() });
      setResults(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Domain search failed");
    } finally {
      setLoading(false);
    }
  };

  const buy = (d) => {
    addToCart({
      sku: "host-pro",
      name: `Domain ${d.domain} + Pro Hosting`,
      price: d.price + 4999,
      meta: { domain: d.domain },
    });
    toast.success(`${d.domain} added with Pro Hosting`);
  };

  return (
    <div className={`w-full ${compact ? "" : "max-w-3xl mx-auto"}`}>
      <form onSubmit={search} className="glass rounded-2xl p-2.5 flex items-center gap-2" data-testid="domain-search-form">
        <div className="pl-3 text-cyan-400"><Search size={18} /></div>
        <input
          data-testid="domain-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Find your perfect domain (e.g. nextgenstudio)"
          className="flex-1 bg-transparent border-none outline-none text-white placeholder:text-gray-500 py-3"
        />
        <button type="submit" disabled={loading} className="btn-primary !py-2.5 !px-5 flex items-center gap-2" data-testid="domain-search-button">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          Search
        </button>
      </form>

      {!compact && (
        <div className="flex flex-wrap gap-2 mt-4 justify-center">
          {[".com ₹999", ".ai ₹12,999", ".io ₹4,999", ".digital ₹2,499", ".dev ₹1,599", ".in ₹599"].map((t) => (
            <span key={t} className="tag">{t}</span>
          ))}
        </div>
      )}

      {results.length > 0 && (
        <div className="mt-6" data-testid="domain-results">
          <div className="flex items-center gap-2 mb-3 text-xs">
            {anyLive ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-400/10 text-emerald-300 border border-emerald-400/30" data-testid="dnapi-live-badge">
                <Zap size={12}/> Live availability via DomainNameAPI
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-400/10 text-amber-300 border border-amber-400/30" data-testid="dnapi-mock-badge">
                <AlertCircle size={12}/> Preview mode — connect DomainNameAPI for live data
              </span>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
          {results.map((d) => (
            <div key={d.domain} className="card p-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-white font-semibold text-lg">{d.domain}</span>
                  {d.premium && <span className="tag !text-amber-300 !border-amber-400/40 !bg-amber-400/5">Premium</span>}
                  {d.live && <span className="tag !text-emerald-300 !border-emerald-400/40 !bg-emerald-400/5">Live</span>}
                </div>
                <div className="flex items-center gap-2 mt-1 text-sm">
                  {d.available ? (
                    <span className="text-emerald-400 flex items-center gap-1"><Check size={14}/> Available</span>
                  ) : (
                    <span className="text-rose-400 flex items-center gap-1"><XIcon size={14}/> Taken</span>
                  )}
                  <span className="text-gray-500">·</span>
                  <span className="text-gray-300">₹{d.price.toLocaleString()}/yr</span>
                </div>
              </div>
              {d.available && (
                <button onClick={() => buy(d)} className="btn-ghost !py-2 !px-3 text-sm" data-testid={`buy-${d.domain}`}>
                  Add
                </button>
              )}
            </div>
          ))}
          </div>
          {live === false && (
            <p className="mt-3 text-xs text-slate-500" data-testid="dnapi-config-note">
              Tip: Set <code className="text-slate-400">DOMAIN_API_KEY</code> and <code className="text-slate-400">DOMAIN_API_RESELLER_ID</code> in backend env to enable live registration.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
